import PropTypes from 'prop-types';
import React, { useContext, useState, useMemo, useCallback } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch } from 'react-redux';

import { LoadingContext, M_StateActions, MTable } from 'src/M_Components';

export default function ContinentConfig({ roles, onCreateTab, toastMessages }) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [refresh, setRefresh] = useState(false);

    // Memoize to avoid creating a new array on every render,
    // which would otherwise trigger unnecessary rerenders downstream.
    const removeContextItems = useMemo(() => ['Print', 'Copy'], []);

    // Centralize the CRUD route so we don't repeat the string across functions.
    const CRUD_ROUTE = '/home/countries/continentConfigCrud';

   
    //====================================================================================================================
    // openContinentCrud:
    // - Single, stable (useCallback) function that opens the CRUD tab for Continents.
    // - Persists the operation and, when required, the selected row into global state.
    // - Validates presence of 'data' for View/Edit/Copy operations.
    // - Computes tab labels using i18n keys to keep logic in one place and avoid prop-drilling labels.
    // - Using a stable callback prevents child components (like MTable) from re-rendering due to changing props.
    //====================================================================================================================
    const openContinentCrud = useCallback(
        (operation, data) => {
            // Persist operation globally for the CRUD page to consume.
            dispatch(M_StateActions.addM_State({ key: 'operation', value: operation }));

            // Some operations require a selected row; validate and store it.
            if (['Edit', 'Copy', 'View'].includes(operation)) {
                if (!data) {
                    toastMessages('error', '', t('SelectedLine'));
                    return;
                }
                dispatch(M_StateActions.addM_State({ key: 'ContinentLine', value: data }));
            }

            // Compute tab label based on operation.
            const tabLabel =
                operation === 'Create'
                    ? t('LabelNewContinent')
                    : operation === 'View'
                    ? `${t('LabelViewContinent')} "${data?.Name ?? ''}"`
                    : `${t('LabelEditContinent')} "${data?.Name ?? ''}"`;

            // Open (or focus) the CRUD tab.
            onCreateTab(CRUD_ROUTE, tabLabel);
        },
        [dispatch, onCreateTab, t, toastMessages]
    );

    //====================================================================================================================
    // replaceFunctions:
    // - Memoized map handed to MTable to wire context actions (View/Create/Edit) to our stable handler.
    // - Using useMemo ensures the Map identity is stable across renders,
    //   avoiding unnecessary re-renders of MTable and preventing duplicate fetches.
    //=====================================================================================================================
    const replaceFunctions = useMemo(
        () =>
            new Map([
                ['View', (row) => openContinentCrud('View', row)],
                ['Create', () => openContinentCrud('Create')],
                ['Edit', (row) => openContinentCrud('Edit', row)],
            ]),
        [openContinentCrud]
    );

    return (
        <div className='divGlobalSettings'>
            <div className='subTitleSettings'>
                <div>
                    <div style={{ color: ctx.secondaryColor }}>{t("Continents")}</div>
                    <div className='labelSubtitleSettings'>{t("InfoContinents")}</div>
                </div>
            </div>
            <MTable //dynamic table for Continents
                filtersOnTable
                entityName={'Continents'}
                roleName={'Locations'}
                rolePermissions={roles?.map(vl => vl.Value)} // Array with roles for the Locations module
                removeContextItems={removeContextItems} // Context menu items to remove
                refresh={refresh}
                setRefresh={setRefresh}
                useSessionStorage={false}
                replaceFunctions={replaceFunctions} // Map of functions to replace default MTable actions
            />
        </div>
    );
}

ContinentConfig.propTypes = {
    roles: PropTypes.array,
    onCreateTab: PropTypes.func,
    toastMessages: PropTypes.func
};
